import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Skeleton,
  Checkbox,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import { 
  Visibility, 
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Search,
  Delete as DeleteIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Deselect as DeselectIcon,
  DeleteSweep as DeleteSweepIcon,
  RocketLaunch as RocketIcon,
  Assessment as AssessmentIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import EmptyState from '@signals/app-shell/components/common/EmptyState';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '@signals/app-shell/services/api';
import EnhancedResultsViewer from '../components/EnhancedResultsViewer';
import QuickLaunchTab from '../components/QuickLaunchTab';
import { AnalysisJob } from '@signals/app-shell/types';
import { formatDateRange } from '@signals/app-shell/utils/dateUtils';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useCourseLabels } from '@signals/app-shell/hooks/useCourseLabels';

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const ResultsPage: React.FC = () => {
  const { plural, pluralLower } = useParticipantLabels();
  const { courseSingular } = useCourseLabels();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500); // 500ms debounce
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9); // Reduced from 12 to 9
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<AnalysisJob | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [userFilter, setUserFilter] = useState<'mine' | 'all'>('mine');
  const [mainTab, setMainTab] = useState<'jobs' | 'quickLaunch'>('jobs');
  
  // Selection state for bulk operations
  const [selectedJobs, setSelectedJobs] = useState<Set<number>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  const { data: jobsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['analysisJobs', page, rowsPerPage, debouncedSearch, userFilter],
    queryFn: async () => {
      if (userFilter === 'all') {
        // Fetch all users' jobs
        const response = await apiService.getAllAnalysisJobs();
        // Filter and paginate on frontend
        const filtered = debouncedSearch 
          ? response.filter((j: AnalysisJob) => 
              j.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
              j.id.toString().includes(debouncedSearch)
            )
          : response;
        const start = page * rowsPerPage;
        const jobs = filtered.slice(start, start + rowsPerPage);
        return { jobs, total: filtered.length, page, limit: rowsPerPage, pages: Math.ceil(filtered.length / rowsPerPage) };
      } else {
        // Fetch only my jobs
        return apiService.getAnalysisJobs(page, rowsPerPage, debouncedSearch || undefined);
      }
    },
    staleTime: 3000, // Consider data fresh for 3 seconds
    refetchInterval: 10000, // Auto-refresh every 10 seconds (reduced frequency)
  });

  // Extract jobs and pagination data
  const jobs = jobsResponse?.jobs || [];
  const totalJobs = jobsResponse?.total || 0;
  
  // Use jobs directly - server-side pagination
  const paginatedJobs = jobs;
  const filteredJobs = { length: totalJobs }; // For pagination display

  // Handle page change
  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle delete job
  const handleDeleteJob = async () => {
    if (!jobToDelete) return;
    
    try {
      setDeleting(true);
      await apiService.deleteAnalysisJob(jobToDelete.id);
      
      // Close dialog and reset state first
      setDeleteDialogOpen(false);
      setJobToDelete(null);
      
      // Invalidate cache and force refresh
      await queryClient.invalidateQueries({ queryKey: ['analysisJobs'] });
    } catch (err: any) {
      console.error('Failed to delete job:', err);
      // Could add error toast here
    } finally {
      setDeleting(false);
    }
  };

  // Handle fix stuck jobs
  const handleFixStuckJobs = async () => {
    try {
      const result = await apiService.fixStuckJobs();
      
      // Show success message
      if (result.fixed_jobs.length > 0) {
        alert(`Fixed ${result.fixed_jobs.length} stuck job(s): ${result.fixed_jobs.join(', ')}`);
      } else {
        alert('No stuck jobs found');
      }
      
      // Refresh the list
      refetch();
    } catch (error) {
      console.error('Failed to fix stuck jobs:', error);
      alert('Failed to fix stuck jobs. Please try again.');
    }
  };

  // Open delete confirmation
  const openDeleteDialog = (job: AnalysisJob) => {
    setJobToDelete(job);
    setDeleteDialogOpen(true);
  };

  // Selection handlers
  const handleSelectJob = (jobId: number) => {
    setSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedJobs.size === paginatedJobs.length) {
      // Deselect all
      setSelectedJobs(new Set());
    } else {
      // Select all on current page
      setSelectedJobs(new Set(paginatedJobs.map((job: AnalysisJob) => job.id)));
    }
  };

  const handleDeselectAll = () => {
    setSelectedJobs(new Set());
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedJobs.size === 0) return;
    
    try {
      setBulkDeleting(true);
      
      // Delete all selected jobs
      const deletePromises = [...selectedJobs].map((jobId: number) =>
        apiService.deleteAnalysisJob(jobId).catch(err => {
          console.error(`Failed to delete job ${jobId}:`, err);
          return null; // Continue with other deletions even if one fails
        })
      );
      
      await Promise.all(deletePromises);
      
      // Clear selection and close dialog first
      setSelectedJobs(new Set());
      setBulkDeleteDialogOpen(false);
      
      // Invalidate cache and force refresh
      await queryClient.invalidateQueries({ queryKey: ['analysisJobs'] });
    } catch (err: any) {
      console.error('Bulk delete failed:', err);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Check if all jobs on current page are selected
  const allSelected = paginatedJobs.length > 0 && selectedJobs.size === paginatedJobs.length;
  const someSelected = selectedJobs.size > 0 && selectedJobs.size < paginatedJobs.length;

  // Fix stuck jobs
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <HourglassEmpty color="warning" />;
      default:
        return <></>;
    }
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" | "default" => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (selectedJob) {
    return (
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <Box sx={{ mb: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => setSelectedJob(null)}
            sx={{ mb: 2 }}
          >
            ← Back to Results List
          </Button>
        </Box>
        <EnhancedResultsViewer jobId={selectedJob} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box sx={{ width: 4, alignSelf: 'stretch', minHeight: 44, borderRadius: 1, bgcolor: 'secondary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, lineHeight: 1.15, color: 'text.primary' }}>
              Analysis Results
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              View all analysis jobs and completed reports
            </Typography>
          </Box>
        </Box>
        <Box>
          {/* Hide Refresh and New Analysis buttons when on Quick Launch tab (has its own refresh) */}
          {mainTab !== 'quickLaunch' && (
            <>
              <Tooltip title="Fix stuck jobs">
                <IconButton onClick={handleFixStuckJobs} color="warning">
                  <BuildIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={() => refetch()} color="primary">
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Button 
                variant="contained" 
                onClick={() => navigate('/analysis')}
                sx={{ ml: 2 }}
              >
                New Analysis
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Main Tab Navigation */}
      <Tabs
        value={mainTab}
        onChange={(_, newValue) => setMainTab(newValue)}
        sx={{ 
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 48,
            textTransform: 'none',
            fontWeight: 600,
          }
        }}
      >
        <Tab 
          value="jobs" 
          label="Analysis Jobs" 
          icon={<AssessmentIcon />} 
          iconPosition="start"
        />
        <Tab 
          value="quickLaunch" 
          label="Quick Launch" 
          icon={<RocketIcon />} 
          iconPosition="start"
          sx={{ 
            color: 'success.main',
            '&.Mui-selected': { color: 'success.main' }
          }}
        />
      </Tabs>

      {/* Quick Launch Tab Content */}
      {mainTab === 'quickLaunch' && (
        <QuickLaunchTab onRefresh={() => refetch()} />
      )}

      {/* Analysis Jobs Tab Content */}
      {mainTab === 'jobs' && (
      <>
      {/* Filter and Search Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <ToggleButtonGroup
              value={userFilter}
              exclusive
              onChange={(_e, newValue) => {
                if (newValue !== null) {
                  setUserFilter(newValue);
                  setPage(0);
                  setSelectedJobs(new Set()); // Clear selection on filter change
                }
              }}
              size="small"
            >
              <ToggleButton value="mine">
                <PersonIcon sx={{ mr: 1 }} />
                My Jobs
              </ToggleButton>
              <ToggleButton value="all">
                <PeopleIcon sx={{ mr: 1 }} />
                All Jobs
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              variant="outlined"
              placeholder={`Search by ID, name, ${pluralLower}...`}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
                setSelectedJobs(new Set()); // Clear selection on search
              }}
              size="small"
              sx={{ flex: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm !== debouncedSearch && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          
          {/* Selection Controls */}
          {!isLoading && paginatedJobs.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Select All ({paginatedJobs.length})
                    </Typography>
                  }
                />
                
                {selectedJobs.size > 0 && (
                  <>
                    <Chip
                      label={`${selectedJobs.size} selected`}
                      color="primary"
                      size="small"
                      onDelete={handleDeselectAll}
                      deleteIcon={<DeselectIcon />}
                    />
                    
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<DeleteSweepIcon />}
                      onClick={() => setBulkDeleteDialogOpen(true)}
                    >
                      Delete Selected
                    </Button>
                  </>
                )}
              </Stack>
            </>
          )}
          
          {debouncedSearch && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing {totalJobs} result{totalJobs !== 1 ? 's' : ''}
            </Typography>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load analysis jobs. Please try again.
        </Alert>
      )}

      {!isLoading && jobs && jobs.length === 0 && !debouncedSearch && (
        <EmptyState
          icon={<AssessmentIcon />}
          title="No analysis results yet"
          description="Run an analysis to see completed jobs and reports here."
          action={{ label: 'New Analysis', icon: <RocketIcon />, onClick: () => navigate('/analysis') }}
        />
      )}

      {!isLoading && debouncedSearch && jobs.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No results found for "{debouncedSearch}". Try a different search term or clear the search to see all results.
        </Alert>
      )}

      <Grid container spacing={3}>
        {isLoading ? (
          // Show skeleton loaders while loading
          Array.from(new Array(rowsPerPage)).map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={`skeleton-${index}`}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Skeleton variant="text" width="40%" height={32} />
                    <Skeleton variant="rectangular" width={80} height={24} />
                  </Box>
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="80%" />
                  <Box sx={{ mt: 2 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="60%" />
                  </Box>
                </CardContent>
                <CardActions>
                  <Skeleton variant="rectangular" width={120} height={36} />
                </CardActions>
              </Card>
            </Grid>
          ))
        ) : (
          paginatedJobs?.map((job: AnalysisJob) => (
          <Grid item xs={12} md={6} lg={4} key={job.id}>
            <Card sx={{ 
              position: 'relative',
              border: selectedJobs.has(job.id) ? '2px solid' : '1px solid',
              borderColor: selectedJobs.has(job.id) ? 'primary.main' : 'divider',
              transition: 'border-color 0.2s'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={selectedJobs.has(job.id)}
                      onChange={() => handleSelectJob(job.id)}
                      size="small"
                      sx={{ p: 0 }}
                    />
                    <Typography variant="h6" component="h2">
                      Analysis #{job.id}
                    </Typography>
                  </Box>
                  <Chip 
                    icon={getStatusIcon(job.status)}
                    label={job.status.toUpperCase()}
                    color={getStatusColor(job.status)}
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>{plural}:</strong> {job.students?.join(', ') || 'All'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Date Range:</strong> {formatDateRange(job.date_range_start, job.date_range_end)}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>{courseSingular}:</strong> {job.course_id || 'N/A'}
                </Typography>

                {job.user_id && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Created by: <strong>{job.user_id.split('@')[0]}</strong>
                    </Typography>
                  </Box>
                )}
                
                {job.start_time && (
                  <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                    Started: {new Date(job.start_time).toLocaleString()}
                  </Typography>
                )}
                {job.end_time && (
                  <Typography variant="caption" display="block">
                    Completed: {new Date(job.end_time).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
              
              <CardActions sx={{ justifyContent: 'space-between' }}>
                <Box>
                  {job.status === 'completed' && (
                    <Button 
                      size="small" 
                      startIcon={<Visibility />}
                      onClick={() => setSelectedJob(job.id)}
                    >
                      View Results
                    </Button>
                  )}
                  {job.status === 'running' && (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                      Analysis in progress...
                    </Typography>
                  )}
                  {job.status === 'failed' && job.error_message && (
                    <Typography variant="body2" color="error" sx={{ pl: 1 }}>
                      Error: {job.error_message}
                    </Typography>
                  )}
                </Box>
                
                {/* Delete button - show for all jobs (cancel if running, delete otherwise) */}
                <Tooltip title={job.status === 'running' ? "Cancel & Delete" : "Delete Analysis"}>
                  <IconButton 
                    size="small"
                    color="error"
                    onClick={() => openDeleteDialog(job)}
                    disabled={deleting && jobToDelete?.id === job.id}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))
        )}
      </Grid>

      {/* Pagination */}
      {filteredJobs.length > 0 && (
        <Box sx={{ 
          mt: 4, 
          display: 'flex', 
          justifyContent: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredJobs.length)} of {filteredJobs.length} analysis jobs
          </Typography>
          <TablePagination
            component="div"
            count={filteredJobs.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[6, 12, 24, 48]}
            labelRowsPerPage="Jobs per page:"
            showFirstButton
            showLastButton
            sx={{ 
              '& .MuiTablePagination-toolbar': { 
                minHeight: '52px',
                paddingLeft: { xs: 0, sm: 2 },
                paddingRight: { xs: 0, sm: 2 }
              },
              '& .MuiTablePagination-selectLabel': { 
                margin: 0,
                fontSize: '0.875rem'
              },
              '& .MuiTablePagination-displayedRows': { 
                margin: 0,
                fontSize: '0.875rem'
              }
            }}
          />
        </Box>
      )}
      </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Analysis Job?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to permanently delete this analysis job and all its results? 
            This action cannot be undone.
            <br /><br />
            <strong>Job:</strong> Analysis #{jobToDelete?.id}
            <br />
            <strong>{plural}:</strong> {jobToDelete?.students?.join(', ') || 'All'}
            <br />
            <strong>Created:</strong> {jobToDelete?.start_time ? new Date(jobToDelete.start_time).toLocaleDateString() : 'Unknown'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteJob} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => !bulkDeleting && setBulkDeleteDialogOpen(false)}
        aria-labelledby="bulk-delete-dialog-title"
        aria-describedby="bulk-delete-dialog-description"
      >
        <DialogTitle id="bulk-delete-dialog-title" sx={{ color: 'error.main' }}>
          <DeleteSweepIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Delete {selectedJobs.size} Analysis Jobs?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="bulk-delete-dialog-description">
            Are you sure you want to permanently delete <strong>{selectedJobs.size}</strong> analysis job{selectedJobs.size !== 1 ? 's' : ''} and all their results? 
            This action cannot be undone.
            <br /><br />
            <strong>Job IDs to delete:</strong> {[...selectedJobs].sort((a: number, b: number) => a - b).join(', ')}
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will permanently remove all selected analyses and their results from the system.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBulkDeleteDialogOpen(false)} 
            disabled={bulkDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkDelete} 
            color="error" 
            variant="contained"
            disabled={bulkDeleting}
            startIcon={bulkDeleting ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
          >
            {bulkDeleting ? `Deleting ${selectedJobs.size}...` : `Delete ${selectedJobs.size} Jobs`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResultsPage;
